import React, {useEffect, useState} from 'react';
import {Button, ButtonVariant} from '@amzn/stencil-react-components/button';
import {FlyoutContent, WithFlyout} from '@amzn/stencil-react-components/flyout';
import {InputWrapper, LabelPosition} from '@amzn/stencil-react-components/input';
import {Radio} from '@amzn/stencil-react-components/radio';
import {Col} from '@amzn/stencil-react-components/layout';
import {Text} from '@amzn/stencil-react-components/text';
import i18n from 'i18next';
import {useTranslation} from 'react-i18next';
import TranslationKeys from 'src/constants/translation-keys';
import { IconGlobeSmall } from '@amzn/stencil-react-icons';
import I18nHelper from 'src/helpers/i18nHelper';
import {UseCase} from 'src/components/common/UseCase';
import { token } from '@amzn/stencil-design-tokens/js/web/utils';

const i18nConfig = require('../../../configuration/i18nConfig');

type Props = {
    useCase?: UseCase;
}

export default function LanguageSelectFlyout({useCase} : Props) {
    const { t } = useTranslation();

    const [selectedLang, setSelectedLang] = useState<string | undefined>(undefined);
    const [userTriggeredChangeLanguage, setUserTriggeredChangeLanguage] = useState(false);

    function changeLanguage(value?: string) {
        i18n.changeLanguage( i18nConfig.langMap.get(value), () => {} ).then();
    }

    useEffect(() => {
        if (userTriggeredChangeLanguage && selectedLang) {
            changeLanguage(selectedLang);
        }
        // reset
        setUserTriggeredChangeLanguage(false);
        setSelectedLang(undefined);
    }, [userTriggeredChangeLanguage]);

    function renderLanguageList() {
        const selectedLangIndex = I18nHelper.getIndexOfSelectedLanguage();
        return (
            <Col gridGap="S300">
                <Text fontSize="T500">
                    {t(TranslationKeys.FOOTER.LANGUAGE.CHANGE_LANGUAGE_HEADER)}
                </Text>
                { i18nConfig.langArray.map((val: string, index: number) => {
                    return <InputWrapper
                        id={"radio-trailing" + index}
                        labelText={val}
                        key={"radio-wrapper" + index}
                        labelPosition={LabelPosition.Trailing}>
                        {(inputProps) => <Radio key={"radio" + index}
                                              name="radio"
                                              data-testid={ "languageSelectionRadio" + index }
                                              defaultChecked={ selectedLangIndex === index }
                                              onClick={ () => setSelectedLang(val) }
                                              {...inputProps} />}
                    </InputWrapper>
                }) }
            </Col>
        )
    }

    const renderFlyout = ({ close }: { close: () => void }) => (
        <FlyoutContent
            titleText={t(TranslationKeys.FOOTER.LANGUAGE.CHOOSE_LANGUAGE)}
            onCloseButtonClick={close}
            buttons={[
                <Button data-testid="language-flyout-close-button"
                        onClick={
                            () => {
                                setUserTriggeredChangeLanguage(true);
                                close();
                            }
                        }
                        key={'close-button-key'}
                        variant={ButtonVariant.Primary}>
                     {t(TranslationKeys.FOOTER.LANGUAGE.CHANGE_LANGUAGE)}
                </Button>
            ]}>
            { renderLanguageList() }
        </FlyoutContent>
    );

    const labelText = useCase === UseCase.ALUMNI ? t(TranslationKeys.UI.ALUMNI.LANGUAGE_FOOTER) : t(TranslationKeys.FOOTER.LANGUAGE.CHANGE_LANGUAGE);

    return (
        <WithFlyout renderFlyout={renderFlyout}>
            {({open}) => (
                <Button variant={ButtonVariant.TertiaryInline}
                        data-testid="language-flyout-button"
                        onClick={open}
                        style={{color: token('color.link.primary-default')}}
                        icon={<IconGlobeSmall aria-hidden={true} />}
                        aria-label={labelText}>
                    <span data-testid="footer-text-language">{labelText}</span>
                </Button>
            )}
        </WithFlyout>
    );
}
